import { SyntheticEvent, useCallback, useMemo } from 'react';
import cls from 'classnames';
import PlayFill from 'icon/play_fill.svg';
import Heart from 'icon/heart.svg';
import _clamp from 'lodash/clamp';
import { Article } from 'shared/models/legacy';
import { useNumFormatter } from '~/utils/format';
import { useViewTracking } from '~/utils/use-view-tracking';
import { ClickPosition } from '~/utils/click-position';
import { useAtomValue } from 'jotai';
import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';
import { $CurrentPageModule } from '~/models/current-page-module';
import useReflowClick from '~/utils/use-reflow-click';
import { usePageActive } from '~/utils/active/use-page-active';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { EArticleClass } from 'shared/models';

import './style.scss';

interface ArticleRecommendCardProps {
  article: Article;
  rank: number;
  hitGpt?: boolean;
}

export default function ArticleRecommendCard(props: ArticleRecommendCardProps): JSX.Element {
  const formatNum = useNumFormatter();

  const { article, rank, hitGpt = false } = props;

  const { imageList, articleClass, title, groupId, largeImage, author, articleLikes, imprId, gptInfo = {} } = article;

  const { title: gptTitle = '' } = gptInfo;

  const isMobileUser = useAtomValue($IsMobileUser);

  const prefix = useScopedAtomValue($CurrentPositionPrefix);

  const currentPageModule = useScopedAtomValue($CurrentPageModule);

  const finalTitle = useMemo(() => {
    if (hitGpt) {
      return gptTitle || title;
    }
    return title;
  }, [hitGpt, gptTitle, title]);

  const active = usePageActive();

  const cardObservedRef = useViewTracking<HTMLDivElement>({
    onView: useCallback(() => {
      if (isMobileUser) {
        window.Tea('wap_cta_show', {
          current_page_module: currentPageModule,
          current_position: prefix + ClickPosition.related_post,
          rank,
        });
      }

      window.Tea('wap_related_post_show', {
        current_page_module: currentPageModule,
        current_position: prefix + ClickPosition.related_post,
        group_id: groupId,
        media_id: author.userId,
        article_class: articleClass,
        impr_id: imprId,
        rank,
      });
    }, [isMobileUser]),
  });

  const handleClickArticle = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_page_module: currentPageModule,
        current_position: prefix + ClickPosition.related_post,
        page_click_id: groupId,
        rank,
      }),
      reflowBehavior: () => {
        active({
          schemaOption: {
            pageType: articleClass === EArticleClass.VIDEO ? 'video' : 'article',
            groupId,
            authorId: author.userId,
          },
          ampExtra: {
            enter_position: prefix + ClickPosition.related_post,
          },
        });
      },
    },
    [active, groupId, articleClass, author],
  );

  const aspectRatio = useMemo(() => {
    switch (articleClass) {
      case EArticleClass.GALLERY:
        return _clamp(imageList[0].width / imageList[0].height, 3 / 4, 4 / 3);
      case EArticleClass.ARTICLE:
      case EArticleClass.VIDEO:
      default:
        return _clamp(largeImage.width / largeImage.height, 3 / 4, 4 / 3);
    }
  }, [article]);

  const coverUrl = useMemo(() => {
    switch (article.articleClass) {
      case EArticleClass.GALLERY:
        return article.imageList[0].url;
      case EArticleClass.ARTICLE:
      case EArticleClass.VIDEO:
      default:
        return article.largeImage.url;
    }
  }, [article]);

  return (
    <div className="article-recommend-card" onClick={handleClickArticle} ref={cardObservedRef}>
      <div
        className={cls('article-cover', {
          video: article.articleClass === EArticleClass.VIDEO,
        })}
        style={{ paddingBottom: `${(1 / aspectRatio) * 100}%` }}
      >
        <img loading="lazy" src={coverUrl} onContextMenu={(e: SyntheticEvent) => e.preventDefault()} />
        {article.articleClass === EArticleClass.VIDEO && <PlayFill className="play-icon" />}
      </div>
      <div className="article-content">
        <div className="article-title">{finalTitle}</div>
        <div className="article-desc">
          <img
            loading="lazy"
            className="author-avatar"
            src={article.author.avatar}
            onContextMenu={(e: SyntheticEvent) => e.preventDefault()}
          />
          <span className="author-name">{article.author.nickName}</span>
          <div className="digg">
            <Heart />
            {Boolean(articleLikes) && <span>{formatNum(articleLikes)}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
